import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import RenderHTML from "../../../components/ui-components/RenderHTML"
import type { EliteLandingPlan } from '../planTypes'
import {
  EliteLandingPageState,
  EliteOnboardingRenewalStatus,
  type EliteInsightsCta,
  type EliteOnboardingStatusResponse,
  type ElitePlanOfferType,
  type EliteReferralReward
} from "../../../types/misc.types"
import { EliteConsentTextContent, hasEliteConsentText } from '../../../components/elite/EliteConsentTextContent'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { lightImpactVibrate, postCTA as postCta } from "../../../utils/appBridge"
import { useNewEliteLandingAnalyticsScreen } from '../analytics/NewEliteLandingAnalyticsContext'
import {
  NewEliteLandingBottomSheetName,
  NewEliteLandingEventId,
  trackNewEliteBottomSheetLoaded,
  trackNewEliteButtonTap,
  trackNewEliteUserClicked
} from '../analytics/newEliteLandingAnalytics'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { convertEpochToDate, roundOffAndFormatNumberWithCurrency } from "../../../utils/utils"
import { NewEliteLandingPlanSelectorSkeleton } from './NewEliteLandingPlanSelectorSkeleton'
import NewEliteFamilyPrimaryNonEliteOfferCard, { EliteFamilyOfferCardIntent } from './family/NewEliteFamilyPrimaryNonEliteOfferCard'
import {
  defaultNonFamilyOnboardingViewProps,
  type FamilyOnboardingViewProps
} from '../pages/family/familyOnboardingViewProps'
import { isYearlyPlanTenure } from '../eliteOnboardingPlanTenure'
import { getEligiblePlanCtaAmount } from '../../../components/elite/dashboard/eliteOnboardingFocusPlan'
import {
  EliteSpecialOfferGiftBanner,
  isSignupTenureBasedOffer,
  isTenureBasedOfferType,
  resolveTenureBasedBaseMonths
} from '../../../components/elite/EliteSpecialOfferGiftBanner'
import {
  EliteReferralRewardBanner,
  EliteReferralGiftIcon,
  hasEliteReferralReward as _hasEliteReferralReward
} from '../../../components/elite/EliteReferralRewardBanner'
import { getEliteBuyCtaEventExtras, type EliteEventExtras } from '../../../utils/eliteEventExtras'
export { isYearlyPlanTenure } from '../eliteOnboardingPlanTenure'

type PlanConfig = {
  id: string
  type: EliteLandingPlan
  title: string
  subtitle?: string
  currentPrice: string
  previousPrice?: string
  showStrikethrough: boolean
  bannerType?: 'SAVE_44' | 'LIMITED_TIME_14_MONTH' | 'TENURE_BASED' | 'PRICE_BASED'
  offerType?: ElitePlanOfferType
  signupOffer?: boolean
  specialOfferBanner?: {
    offerValidTill?: number
    extraMonths?: number
    baseMonths?: number
  }
  referralReward?: EliteReferralReward
}

type EligiblePlan = EliteOnboardingStatusResponse['eligiblePlans'][number]

type NewEliteLandingPlanSelectorProps = {
  familyConfig?: FamilyOnboardingViewProps
  selectedPlan: EliteLandingPlan
  onPlanChange: (plan: EliteLandingPlan) => void
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans']
  cta?: EliteOnboardingStatusResponse['cta']
  eliteLandingPageState?: EliteOnboardingStatusResponse['eliteLandingPageState']
  selectedPlanDetails?: EliteOnboardingStatusResponse['selectedPlanDetails']
  activationExpiryDate?: number
  consentText?: string
  consentTextList?: EliteOnboardingStatusResponse['consentTextList']
  isStatusLoading?: boolean
  /** Primary Elite buying for a secondary family member: CTA can include their first name. */
  familyPurchaseForSecondary?: {
    memberCtaName: string
  }
  /** Renew copy (CTA, section title) when embedding in dashboard renewal; merges over `eliteLandingPageState` config. */
  eliteRenewalFlow?: boolean
  /** Show a fixed bottom CTA when the inline primary CTA scrolls out of view (landing page). */
  enableFloatingPrimaryCta?: boolean
  /** When set, controls sticky CTA visibility from the page scroll sentinel instead of inline IntersectionObserver. */
  isFloatingPrimaryCtaVisible?: boolean
  /** Ref attached to the floating primary CTA container (for measuring height). */
  floatingPrimaryCtaRef?: RefObject<HTMLDivElement>
  /** Ref attached to the inline primary CTA (About Elite FAB bottom-band observer). */
  inlinePrimaryCtaRef?: RefObject<HTMLDivElement>
  /** Fired when primary CTA disabled state changes (e.g. rebalancing section CTAs). */
  onPrimaryCtaDisabledChange?: (disabled: boolean) => void
  autoPayStatus?: EliteOnboardingStatusResponse['subscriptionDetails']['autoPayStatus']
  renewalStatus?: NonNullable<EliteOnboardingStatusResponse['renewal']>['status']
  referralReward?: EliteOnboardingStatusResponse['referralReward']
}

type NewEliteLandingPlanStateConfig = {
  ctaLabel?: string
  /** When set, CTA copy follows the plan the user has selected (or the only visible plan). */
  ctaLabelByPlan?: Partial<Record<EliteLandingPlan, string>>
  isCtaDisabled?: boolean
  topLabel?: string
  supportText?: string
  supportTextType?: 'TEXT' | 'PANEL'
  showOnlyYearlyPlan?: true
  useSelectedPlanDetailsForDisplay?: true
  suppressPlanSelectionChrome?: true
  paymentSuccessTopBanner?: true
  pendingPaymentTopLabelClock?: true
  planSectionTitle?: string
  useRenewCtaLabel?: true
  singlePlanBannerLabel?: string
}

const planBannerGradientClass = 'bg-gradient-to-r from-[#CC5C2D] to-[#D16B3F] text-white'
const planBannerPeachClass = 'bg-[#FFEBCF] text-[#65321D]'

/** Appended to supportText in config for entries that open the eSign learn-more sheet (trusted HTML only). */
export const elitePlanSupportLearnMoreLinkHtml =
  ' <span data-elite-plan-learn-more="1" class="inline cursor-pointer underline">Learn more</span>'

const defaultPlanCtaLabelBySelectedPlan: Record<EliteLandingPlan, string> = {
  yearly: 'Upgrade to Elite Yearly',
  monthly: 'Upgrade to Elite Monthly'
}

const renewalAutopaySupportTextByStatus: Partial<Record<EliteOnboardingRenewalStatus, string>> = {
  [EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING]:
    "Your last Autopay payment failed. We're retrying automatically. If any amount was deducted, it'll be refunded within 3 working days.",
  [EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED]:
    "Your last Autopay payment failed. If any amount was deducted, it'll be refunded within 3 working days.",
}

export const newEliteLandingPlanStateConfig: Partial<Record<`${EliteLandingPageState}`, NewEliteLandingPlanStateConfig>> = {
  [EliteLandingPageState.PAYMENT_PENDING]: {
    ctaLabelByPlan: {
      yearly: 'Get Elite Yearly',
      monthly: 'Get Elite Monthly'
    },
    isCtaDisabled: true,
    topLabel: 'Awaiting payment confirmation',
    pendingPaymentTopLabelClock: true,
    supportText: 'Awaiting payment confirmation from bank. If payment fails, your amount will be refunded in 3 working days.',
    supportTextType: 'TEXT',
    useSelectedPlanDetailsForDisplay: true,
    suppressPlanSelectionChrome: true
  },
  [EliteLandingPageState.PAYMENT_FAILED]: {
    supportText: 'Your last payment failed. Any amount deducted will be refunded in 3 working days. You can try making the payment again.',
    supportTextType: 'TEXT'
  },
  [EliteLandingPageState.RE_INITIATE_PAYMENT]: {
    supportText: 'Your last payment failed. Any amount deducted will be refunded in 3 working days. You can try making the payment again.',
    supportTextType: 'TEXT'
  },
  [EliteLandingPageState.PAYMENT_COMPLETED_SUBSCRIPTION_PENDING]: {
    ctaLabel: 'Complete e-Sign',
    topLabel: 'Payment successful',
    paymentSuccessTopBanner: true,
    supportText:
      'Your payment was successful. Complete eSign now to activate your Elite membership.' + elitePlanSupportLearnMoreLinkHtml,
    supportTextType: 'TEXT',
    useSelectedPlanDetailsForDisplay: true,
    suppressPlanSelectionChrome: true
  },
  [EliteLandingPageState.PAYMENT_COMPLETED_SUBSCRIPTION_PENDING_WARNING]: {
    ctaLabel: 'Complete e-Sign',
    topLabel: 'Payment successful',
    paymentSuccessTopBanner: true,
    supportText:
      'Your payment was successful. Complete eSign before {activationExpiryDate} to activate your Elite membership.' +
      elitePlanSupportLearnMoreLinkHtml,
    supportTextType: 'TEXT',
    useSelectedPlanDetailsForDisplay: true,
    suppressPlanSelectionChrome: true
  },
  [EliteLandingPageState.REFUND_DUE_TO_ESIGN_PENDING]: {
    supportText:
      "Elite membership could not be activated since eSign wasn't completed. Your amount will be refunded within 3-5 working days.",
    supportTextType: 'TEXT'
  },
  [EliteLandingPageState.PLAN_EXPIRED]: {
    planSectionTitle: 'RENEW YOUR PLAN',
    useRenewCtaLabel: true,
    singlePlanBannerLabel: 'Renew and get 2 extra months'
  },
  // Primary not Elite — family secondary cannot buy (elite-plans advisory tab uses this config)
  [EliteLandingPageState.FAMILY_ONBOARDING_NOT_ALLOWED]: {
    isCtaDisabled: true,
    ctaLabel: 'Upgrade to Elite'
  }
}

function resolveSupportTextWithActivationDate(
  supportText: string | undefined,
  activationExpiryDate?: number
): string | undefined {
  if (!supportText) return undefined
  if (!supportText.includes('{activationExpiryDate}')) return supportText
  return supportText.replace('{activationExpiryDate}', convertEpochToDate(activationExpiryDate ?? 0, '%d %b'))
}

function escapeHtmlForSupportText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function resolveSupportTextDisplayHtml(
  supportText: string | undefined,
  activationExpiryDate?: number
): string | undefined {
  const resolved = resolveSupportTextWithActivationDate(supportText, activationExpiryDate)
  if (!resolved) return undefined
  if (resolved.includes('data-elite-plan-learn-more')) {
    return resolved
  }
  return escapeHtmlForSupportText(resolved)
}

function getSupportTextFromPlanStateConfig(
  landingPageState: `${EliteLandingPageState}`
): string | undefined {
  return newEliteLandingPlanStateConfig[landingPageState]?.supportText
}

function getRenewalMappedLandingPageState(
  renewalStatus: NonNullable<EliteOnboardingStatusResponse['renewal']>['status']
): `${EliteLandingPageState}` | undefined {
  if (
    renewalStatus === EliteOnboardingRenewalStatus.RENEWAL_PAYMENT_PENDING ||
    renewalStatus === EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING
  ) {
    return EliteLandingPageState.PAYMENT_PENDING
  }
  if (
    renewalStatus === EliteOnboardingRenewalStatus.RE_INITIATE_PAYMENT ||
    renewalStatus === EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED
  ) {
    return EliteLandingPageState.RE_INITIATE_PAYMENT
  }
  return undefined
}

function getRenewalSupportTextType(
  renewalStatus?: NonNullable<EliteOnboardingStatusResponse['renewal']>['status']
): NewEliteLandingPlanStateConfig['supportTextType'] | undefined {
  if (!renewalStatus) return undefined
  const mappedLandingPageState = getRenewalMappedLandingPageState(renewalStatus)
  if (!mappedLandingPageState) return undefined
  return newEliteLandingPlanStateConfig[mappedLandingPageState]?.supportTextType
}

function getRenewalStatusSupportText(
  renewalStatus?: NonNullable<EliteOnboardingStatusResponse['renewal']>['status']
): string | undefined {
  if (!renewalStatus) return undefined

  const autopaySupportText = renewalAutopaySupportTextByStatus[renewalStatus]
  if (autopaySupportText) return autopaySupportText

  const mappedLandingPageState = getRenewalMappedLandingPageState(renewalStatus)
  return mappedLandingPageState ? getSupportTextFromPlanStateConfig(mappedLandingPageState) : undefined
}

export function resolveRenewalAwareSupportText(
  baseSupportText: string | undefined,
  renewalStatus?: NonNullable<EliteOnboardingStatusResponse['renewal']>['status']
): string | undefined {
  return getRenewalStatusSupportText(renewalStatus) ?? baseSupportText
}

export default function NewEliteLandingPlanSelector({
  familyConfig = defaultNonFamilyOnboardingViewProps,
  selectedPlan,
  onPlanChange,
  eligiblePlans,
  cta,
  eliteLandingPageState,
  selectedPlanDetails,
  activationExpiryDate,
  consentText,
  consentTextList,
  familyPurchaseForSecondary,
  isStatusLoading = false,
  eliteRenewalFlow = false,
  enableFloatingPrimaryCta = false,
  isFloatingPrimaryCtaVisible,
  floatingPrimaryCtaRef,
  inlinePrimaryCtaRef: inlinePrimaryCtaRefFromParent,
  onPrimaryCtaDisabledChange,
  autoPayStatus,
  renewalStatus,
  referralReward
}: NewEliteLandingPlanSelectorProps) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eliteBuyCtaExtras = getEliteBuyCtaEventExtras(
    eligiblePlans.length > 0 ? ({ eligiblePlans } as EliteOnboardingStatusResponse) : undefined
  )
  const [isKycConsentChecked, setIsKycConsentChecked] = useState(true)
  const [isInlinePrimaryCtaVisible, setIsInlinePrimaryCtaVisible] = useState(true)
  const localInlinePrimaryCtaRef = useRef<HTMLDivElement>(null)
  const inlinePrimaryCtaRef = inlinePrimaryCtaRefFromParent ?? localInlinePrimaryCtaRef

  const { openBottomSheet, closeBottomSheet } = useBottomSheet()



  useEffect(() => {
    if (
      !enableFloatingPrimaryCta ||
      isFloatingPrimaryCtaVisible !== undefined ||
      isStatusLoading ||
      familyConfig.isEliteBlockedBecausePrimaryNotElite
    ) {
      setIsInlinePrimaryCtaVisible(true)
      return
    }

    let observer: IntersectionObserver | undefined
    const frameId = requestAnimationFrame(() => {
      const inlineCtaNode = inlinePrimaryCtaRef.current
      if (inlineCtaNode === null) {
        return
      }

      observer = new IntersectionObserver(
        (entries) => {
          setIsInlinePrimaryCtaVisible(entries?.[0]?.isIntersecting ?? false)
        },
        { threshold: 0.2 }
      )
      observer.observe(inlineCtaNode)
    })

    return () => {
      cancelAnimationFrame(frameId)
      observer?.disconnect()
    }
  }, [
    enableFloatingPrimaryCta,
    isFloatingPrimaryCtaVisible,
    isStatusLoading,
    familyConfig.isEliteBlockedBecausePrimaryNotElite,
    eliteLandingPageState,
    selectedPlan,
    consentText,
    consentTextList
  ])

  const pageStateConfig = eliteLandingPageState ? (newEliteLandingPlanStateConfig?.[eliteLandingPageState] ?? {}) : {}
  const renewOverlay = newEliteLandingPlanStateConfig[EliteLandingPageState.PLAN_EXPIRED]

  const stateConfig =
    eliteRenewalFlow && renewOverlay ? { ...pageStateConfig, ...renewOverlay } : pageStateConfig

  const showKycConsent =
    hasEliteConsentText(consentTextList) &&
    eliteLandingPageState === EliteLandingPageState.ONB_NOT_INITIATED
  const isCtaDisabled =
    Boolean(stateConfig?.isCtaDisabled) ||
    (showKycConsent && !isKycConsentChecked) ||
    (eliteRenewalFlow && !(cta as { url?: string } | undefined)?.url)

  useEffect(() => {
    onPrimaryCtaDisabledChange?.(isCtaDisabled)
  }, [isCtaDisabled, onPrimaryCtaDisabledChange])

  if (isStatusLoading) return <NewEliteLandingPlanSelectorSkeleton />
  if (familyConfig.isEliteBlockedBecausePrimaryNotElite) {
    return (
      // <div className='pb-10'>
      <NewEliteFamilyPrimaryNonEliteOfferCard
        familyMemberDisplayName={familyConfig.secondaryUserName}
        eligiblePlans={eligiblePlans}
        eliteIntent={eliteRenewalFlow ? EliteFamilyOfferCardIntent.Renew : EliteFamilyOfferCardIntent.Get}
      />
      // </div>
    )
  }

  const useSelectedDetailsForDisplay = Boolean(stateConfig?.useSelectedPlanDetailsForDisplay)
  const mappedFromSelected = useSelectedDetailsForDisplay
    ? mapSelectedPlanDetailsToPlanConfig(selectedPlanDetails, eligiblePlans)
    : null
  let planConfigs: PlanConfig[]
  if (mappedFromSelected) {
    planConfigs = [mappedFromSelected]
  } else {
    planConfigs = eligiblePlans
      .map((plan) => mapEligiblePlanToPlanConfig(plan))
      .filter((plan): plan is PlanConfig => plan !== null)
  }
  planConfigs = planConfigs.map((plan) => {
    if (plan.type !== 'yearly' || !hasEliteReferralReward(referralReward)) return plan
    return { ...plan, referralReward: referralReward ?? undefined }
  })
  const visiblePlanConfigs = (() => {
    if (!stateConfig?.showOnlyYearlyPlan) return planConfigs
    const yearlyPlans = planConfigs.filter((plan) => plan.type === 'yearly')
    return yearlyPlans.length > 0 ? yearlyPlans : planConfigs
  })()
  const displayedPlanType = visiblePlanConfigs.find((plan) => plan.type === selectedPlan)?.type ?? visiblePlanConfigs[0]?.type ?? selectedPlan
  const displayedPlan = visiblePlanConfigs.find((plan) => plan.type === displayedPlanType) ?? visiblePlanConfigs[0]
  const ctaLabel = resolveCtaLabel({
    stateConfig,
    displayedPlanType,
    displayedPlan,
    familyConfig
  })

  const suppressPlanChrome = Boolean(stateConfig?.isCtaDisabled || stateConfig?.suppressPlanSelectionChrome)

  const effectiveSupportText = resolveRenewalAwareSupportText(stateConfig?.supportText, renewalStatus)
  const renewalSupportTextType = getRenewalSupportTextType(renewalStatus)
  const supportDisplayHtml = resolveSupportTextDisplayHtml(effectiveSupportText, activationExpiryDate)
  const supportTextType = effectiveSupportText
    ? (stateConfig?.supportTextType ?? renewalSupportTextType ?? 'TEXT')
    : stateConfig?.supportTextType
  const supportHasLearnMoreLink = Boolean(supportDisplayHtml?.includes('data-elite-plan-learn-more'))
  const singleVisiblePlan = visiblePlanConfigs.length === 1
  const eligiblePlanForBanner = resolveSelectedPlanForCta(eligiblePlans, displayedPlanType)
  const renewExtraMonthsBannerLabel =
    singleVisiblePlan &&
      stateConfig?.singlePlanBannerLabel &&
      eligiblePlanForBanner &&
      isLimitedTime14MonthOffer(eligiblePlanForBanner)
      ? stateConfig.singlePlanBannerLabel
      : undefined
  const hidePlansForAutoPayRetrying =
    renewalStatus === EliteOnboardingRenewalStatus.RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING

  const handleSupportAreaClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!(e.target as HTMLElement).closest('[data-elite-plan-learn-more]')) return
    lightImpactVibrate()
    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.EsignLearnMoreOpen, {
      ...eliteBuyCtaExtras,
      buttonLabel: 'Learn more',
      bottomSheetName: NewEliteLandingBottomSheetName.EsignLearnMore
    })
    openBottomSheet(
      <EliteEsignSupportLearnMoreBottomSheet
        onDismiss={closeBottomSheet}
        activationExpiryDate={activationExpiryDate}
        buyCtaExtras={eliteBuyCtaExtras}
      />
    )
  }

  const handleCtaClick = () => {
    if (isCtaDisabled) {
      trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.PrimaryCtaDisabled, {
        ...eliteBuyCtaExtras,
        buttonLabel: ctaLabel
      })
      return
    }
    if (!cta) return
    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.PrimaryCta, {
      ...eliteBuyCtaExtras,
      buttonLabel: ctaLabel
    })
    const eligiblePlanForCta = resolveSelectedPlanForCta(eligiblePlans, selectedPlan)
    const selectedPlanId = resolvePlanId(eligiblePlanForCta)
    if (!selectedPlanId) {
      postCta(JSON.stringify(cta))
      return
    }
    postCta(JSON.stringify(updateCtaWithSelectedPlanId(cta, selectedPlanId)))
  }

  const showFloatingPrimaryCta =
    enableFloatingPrimaryCta &&
    (isFloatingPrimaryCtaVisible !== undefined
      ? isFloatingPrimaryCtaVisible
      : !isInlinePrimaryCtaVisible)

  return (
    <>
      <div className='space-y-5'>
        {hidePlansForAutoPayRetrying ? null : (
          <>
            {stateConfig?.planSectionTitle ? (
              <div className='mx-5 flex items-center gap-3'>
                <div className='h-[1px] flex-1 bg-[#E4D8D2]' />
                <p className='text-[13px] font-medium leading-[1.2] text-[#7A3E26] tracking-[0.03em]'>
                  {familyConfig.isFamilyMode ?
                    `Renew ${familyConfig?.secondaryUserName}'s plan` : stateConfig?.planSectionTitle
                  }
                </p>
                <div className='h-[1px] flex-1 bg-[#E4D8D2]' />
              </div>
            ) : null}
            <div className='space-y-3 px-5'>
              {visiblePlanConfigs.map((plan) => (
                <PlanOptionCard
                  familyConfig={familyConfig}
                  key={plan.id}
                  plan={plan}
                  isSelected={selectedPlan === plan.type}
                  onClick={() => {
                    if (suppressPlanChrome) return
                    trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.PlanToggle, {
                      ...eliteBuyCtaExtras,
                      buttonLabel: plan.type,
                      scrollSection: eliteLandingPageState ?? '',
                      responseName: eliteRenewalFlow ? 'renewal' : 'onboarding'
                    })
                    onPlanChange(plan.type)
                  }}
                  topLabel={stateConfig?.topLabel}
                  paymentSuccessTopBanner={stateConfig?.paymentSuccessTopBanner}
                  pendingPaymentTopLabelClock={stateConfig?.pendingPaymentTopLabelClock}
                  suppressPlanChrome={suppressPlanChrome}
                  customYearlyBannerLabel={renewExtraMonthsBannerLabel}
                  singleVisiblePlan={singleVisiblePlan}
                />
              ))}
            </div>
            <div ref={inlinePrimaryCtaRef}>
              <NewEliteLandingPrimaryCtaButton
                label={ctaLabel}
                disabled={isCtaDisabled}
                onClick={handleCtaClick}
              />
            </div>
            {showKycConsent ? (
              <div className='mx-5 flex items-start gap-3 border-t border-[#EBF2F2] pt-3'>
                <div
                  className='mt-1 shrink-0 cursor-pointer'
                  onClick={() => {
                    const next = !isKycConsentChecked
                    setIsKycConsentChecked(next)
                    trackNewEliteButtonTap(analyticsScreen, NewEliteLandingEventId.KycConsentToggle, {
                      ...eliteBuyCtaExtras,
                      buttonLabel: next ? 'Consent Checked' : 'Consent UnChecked'
                    })
                  }}
                >
                  <AnimatedEliteSelectionCheckbox
                    checked={isKycConsentChecked}
                    checkedSvg={eliteKycConsentCheckedBoxSvg}
                    uncheckedSvg={eliteKycConsentUncheckedBoxSvg}
                  />
                </div>
                <div className='max-w-[90%] flex-1 text-[12px] font-[400] leading-[150%] text-[#6A6E6E]'>
                  <EliteConsentTextContent consentTextList={consentTextList} />
                </div>
              </div>
            ) : null}
          </>
        )}
        {supportDisplayHtml && supportTextType === 'PANEL' ? (
          <RenderHTML
            innerHTMLText={supportDisplayHtml}
            className='bg-[#FFE6E1] px-5 py-4 text-sm leading-[1.45] text-[#E77464] text-center font-medium'
            onClick={supportHasLearnMoreLink ? handleSupportAreaClick : undefined}
          />
        ) : null}
        {supportDisplayHtml && supportTextType !== 'PANEL' ? (
          <RenderHTML
            innerHTMLText={supportDisplayHtml}
            className='text-sm leading-[1.45] text-[#E77464] text-center mx-5 font-medium'
            onClick={supportHasLearnMoreLink ? handleSupportAreaClick : undefined}
          />
        ) : null}

      </div>
      {enableFloatingPrimaryCta ? (
        <AnimatePresence>
          {showFloatingPrimaryCta ? (
            <motion.div
              key='floating-primary-cta'
              ref={floatingPrimaryCtaRef}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className='fixed inset-x-0 z-40 mx-auto w-full max-w-[420px] px-5'
              style={{ bottom: `${getBottomPadding() + 16}px` }}
            >
              <NewEliteLandingPrimaryCtaButton
                label={ctaLabel}
                disabled={isCtaDisabled}
                onClick={handleCtaClick}
                className='mx-0 w-full'
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      ) : null}
    </>
  )
}

export const eliteKycConsentUncheckedBoxSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect x="0.75" y="0.75" width="18.5" height="18.5" rx="5.25" stroke="#8E9393" stroke-width="1.5"/>
</svg>`

export const eliteKycConsentCheckedBoxSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <rect width="20" height="20" rx="6" fill="#3D3D3D"/>
  <path d="M5.41504 10.4199L7.9958 12.6781C8.41143 13.0418 9.0432 12.9996 9.40688 12.584L14.5817 6.66992" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

export function resolveCtaLabel({
  stateConfig,
  displayedPlanType,
  displayedPlan,
  familyConfig
}: {
  stateConfig: NewEliteLandingPlanStateConfig
  displayedPlanType: EliteLandingPlan
  displayedPlan?: PlanConfig
  familyConfig: FamilyOnboardingViewProps
}): string {
  const planSpecificCta = stateConfig?.ctaLabelByPlan?.[displayedPlanType]
  if (planSpecificCta) {
    return planSpecificCta
  }
  if (stateConfig?.ctaLabel) {
    return stateConfig.ctaLabel
  }
  if (stateConfig?.useRenewCtaLabel && displayedPlan) {
    return `Renew Elite ${displayedPlan.title} @ ${displayedPlan.currentPrice}+GST`
  }
  const name = familyConfig.secondaryUserName?.trim()
  if (name) {
    const planWord = displayedPlanType === 'yearly' ? 'Yearly' : 'Monthly'
    return `Get Elite ${planWord} for ${name}`
  }
  return defaultPlanCtaLabelBySelectedPlan[displayedPlanType]
}

export function PlanOptionCard({
  familyConfig,
  plan,
  isSelected,
  onClick,
  topLabel,
  suppressPlanChrome,
  paymentSuccessTopBanner,
  pendingPaymentTopLabelClock,
  customYearlyBannerLabel,
  singleVisiblePlan
}: {
  familyConfig: FamilyOnboardingViewProps
  plan: PlanConfig
  isSelected: boolean
  onClick: () => void
  topLabel?: string
  suppressPlanChrome?: boolean
  paymentSuccessTopBanner?: true
  pendingPaymentTopLabelClock?: true
  customYearlyBannerLabel?: string
  singleVisiblePlan?: boolean
}) {
  const shouldShowCustomSinglePlanBanner = Boolean(customYearlyBannerLabel && plan.type === 'yearly')
  const showReferralFooter = plan.type === 'yearly' && hasEliteReferralReward(plan.referralReward)
  // NO_OFFER still shows the default “Special price for a LIMITED TIME” PlanTopBanner
  const hasTopChrome =
    Boolean(topLabel) ||
    Boolean(plan.bannerType) ||
    plan.offerType === 'NO_OFFER' ||
    shouldShowCustomSinglePlanBanner ||
    (familyConfig.isFamilyMode && plan.type === 'yearly')
  const useGradientForSinglePlanBanner =
    Boolean(singleVisiblePlan) && !(topLabel && paymentSuccessTopBanner)
  let bannerContainerClass = planBannerPeachClass
  if (topLabel && !paymentSuccessTopBanner) {
    bannerContainerClass = planBannerPeachClass
  } else if (useGradientForSinglePlanBanner) {
    bannerContainerClass = planBannerGradientClass
  } else if (topLabel) {
    bannerContainerClass = planBannerPeachClass
  } else if (isSelected) {
    bannerContainerClass = planBannerGradientClass
  } else {
    bannerContainerClass = planBannerPeachClass
  }
  if (familyConfig.isFamilyMode) {
    const showSelectedFamilyBanner = isSelected || Boolean(singleVisiblePlan)
    bannerContainerClass = showSelectedFamilyBanner ? planBannerGradientClass : planBannerPeachClass
  }

  const planCardCompoundOutlineClass =
    singleVisiblePlan && !showReferralFooter
      ? ''
      : getPlanCardCompoundOutlineClass(isSelected || Boolean(singleVisiblePlan))

  if (hasTopChrome || showReferralFooter) {
    return (
      <div
        className={`overflow-hidden rounded-xl text-center text-[13px] ${planCardCompoundOutlineClass} ${bannerContainerClass}`}
      >
        {hasTopChrome ? (
          <PlanTopBanner
            familyConfig={familyConfig}
            isSelected={isSelected || Boolean(singleVisiblePlan)}
            bannerType={plan.bannerType}
            offerType={plan.offerType}
            signupOffer={plan.signupOffer}
            specialOfferBanner={plan.specialOfferBanner}
            topLabel={topLabel}
            paymentSuccessTopBanner={paymentSuccessTopBanner}
            pendingPaymentTopLabelClock={pendingPaymentTopLabelClock}
            customYearlyBannerLabel={customYearlyBannerLabel}
          />
        ) : null}
        <PlanOptionBody
          plan={plan}
          isSelected={isSelected}
          onClick={onClick}
          suppressPlanChrome={suppressPlanChrome}
          singleVisiblePlan={singleVisiblePlan}
          borderHandledByParent
          hasBannerAbove={hasTopChrome}
          flushBottom={showReferralFooter}
        />
        {showReferralFooter && plan.referralReward ? (
          <YearlyPlanReferralBonusFooter referralReward={plan.referralReward} />
        ) : null}
      </div>
    )
  }

  return (
    <PlanOptionBody
      plan={plan}
      isSelected={isSelected}
      onClick={onClick}
      suppressPlanChrome={suppressPlanChrome}
      singleVisiblePlan={singleVisiblePlan}
    />
  )
}

export function mapEligiblePlanToPlanConfig(plan: EligiblePlan): PlanConfig | null {
  const resolvedTenure = plan?.effectivePlanTenure ?? plan?.basePlanTenure
  const effectiveFeeAmount = getEligiblePlanCtaAmount(plan)
  const baseFeeAmount = plan?.basePlanFees?.amount ?? 0
  const actualListAmount = plan?.actualPlanPrice?.amount
  const shouldStrikeListPrice =
    typeof actualListAmount === 'number' && actualListAmount > effectiveFeeAmount

  const bannerType = getBannerType(plan)
  const specialOfferBanner = resolveSpecialOfferBanner(plan, bannerType)

  if (isYearlyPlanTenure(resolvedTenure)) {
    return {
      id: plan?.effectivePlanId ?? plan?.basePlanId ?? `yearly-${effectiveFeeAmount}-${baseFeeAmount}`,
      type: 'yearly',
      title: 'Yearly',
      subtitle: plan?.recommended ? 'Recommended' : 'Special price',
      currentPrice: `${roundOffAndFormatNumberWithCurrency(effectiveFeeAmount)}/yr`,
      previousPrice: shouldStrikeListPrice ? `${roundOffAndFormatNumberWithCurrency(actualListAmount ?? 0)}/yr` : undefined,
      showStrikethrough: shouldStrikeListPrice,
      bannerType,
      offerType: plan?.offerType,
      signupOffer: plan?.signupOffer ?? false,
      specialOfferBanner
    }
  }

  if (resolvedTenure?.period === 'MONTH') {
    return {
      id: plan?.effectivePlanId ?? plan?.basePlanId ?? `monthly-${effectiveFeeAmount}-${baseFeeAmount}`,
      type: 'monthly',
      title: 'Monthly',
      subtitle: plan?.recommended ? 'Recommended' : undefined,
      currentPrice: `${roundOffAndFormatNumberWithCurrency(effectiveFeeAmount)}/mo`,
      previousPrice: shouldStrikeListPrice ? `${roundOffAndFormatNumberWithCurrency(actualListAmount ?? 0)}/mo` : undefined,
      showStrikethrough: shouldStrikeListPrice,
      bannerType,
      offerType: plan?.offerType,
      signupOffer: plan?.signupOffer ?? false,
      specialOfferBanner
    }
  }

  return null
}

export function mapSelectedPlanDetailsToPlanConfig(
  details: EliteOnboardingStatusResponse['selectedPlanDetails'] | undefined,
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans']
): PlanConfig | null {
  const feeAmount = details?.membershipFees?.amount ?? 0
  const selectedPlanId = details?.planId ?? ''
  const matchingEligiblePlan = eligiblePlans.find((plan) => {
    if ((plan?.effectivePlanId ?? '') === selectedPlanId) return true
    return (plan?.basePlanId ?? '') === selectedPlanId
  })
  if (matchingEligiblePlan) {
    const mappedPlan = mapEligiblePlanToPlanConfig(matchingEligiblePlan)
    if (!mappedPlan) return null
    return {
      ...mappedPlan,
      id: selectedPlanId || mappedPlan.id,
      currentPrice:
        mappedPlan.type === 'yearly'
          ? `${roundOffAndFormatNumberWithCurrency(feeAmount)}/yr`
          : `${roundOffAndFormatNumberWithCurrency(feeAmount)}/mo`,
      previousPrice: undefined,
      showStrikethrough: false
    }
  }
  const resolvedTenure = details?.effectivePlanTenure ?? details?.basePlanTenure
  const inferredPlanType = resolvePlanTypeFromPlanId(selectedPlanId)

  if (isYearlyPlanTenure(resolvedTenure)) {
    return {
      id: selectedPlanId || 'selected-plan-yearly',
      type: 'yearly',
      title: 'Yearly',
      currentPrice: `${roundOffAndFormatNumberWithCurrency(feeAmount)}/yr`,

      showStrikethrough: false
    }
  }

  if (resolvedTenure?.period === 'MONTH') {
    return {
      id: selectedPlanId || 'selected-plan-monthly',
      type: 'monthly',
      title: 'Monthly',
      currentPrice: `${roundOffAndFormatNumberWithCurrency(feeAmount)}/mo`,
      showStrikethrough: false
    }
  }

  if (inferredPlanType === 'yearly') {
    return {
      id: selectedPlanId || 'selected-plan-yearly',
      type: 'yearly',
      title: 'Yearly',
      currentPrice: `${roundOffAndFormatNumberWithCurrency(feeAmount)}/yr`,
      showStrikethrough: false
    }
  }

  if (inferredPlanType === 'monthly') {
    return {
      id: selectedPlanId || 'selected-plan-monthly',
      type: 'monthly',
      title: 'Monthly',
      currentPrice: `${roundOffAndFormatNumberWithCurrency(feeAmount)}/mo`,
      showStrikethrough: false
    }
  }

  return null
}

export function resolvePlanTypeFromPlanId(planId: string): EliteLandingPlan | undefined {
  const normalizedPlanId = planId.toLowerCase()
  if (!normalizedPlanId) return undefined
  if (normalizedPlanId.includes('month') || normalizedPlanId.includes('monthly') || normalizedPlanId.includes('1m')) {
    return 'monthly'
  }
  if (
    normalizedPlanId.includes('year') ||
    normalizedPlanId.includes('yearly') ||
    normalizedPlanId.includes('12m') ||
    normalizedPlanId.includes('14m')
  ) {
    return 'yearly'
  }
  return undefined
}

export const ELITE_YEARLY_999_OFFER_PRICE_EX_GST = 999

export function isYearly999LimitedTimeOffer(plan: EligiblePlan | undefined): boolean {
  if (!plan) return false
  const tenure = plan?.effectivePlanTenure ?? plan?.basePlanTenure
  if (!isYearlyPlanTenure(tenure)) return false
  const effectiveAmount = plan?.effectivePlanFees?.amount ?? 0
  const baseAmount = plan?.basePlanFees?.amount ?? 0
  return (
    effectiveAmount === ELITE_YEARLY_999_OFFER_PRICE_EX_GST &&
    baseAmount === ELITE_YEARLY_999_OFFER_PRICE_EX_GST
  )
}

export function isTenureBasedSpecialOffer(plan: EligiblePlan | undefined): boolean {
  if (isSignupTenureBasedOffer(plan?.offerType, plan?.signupOffer)) return true
  return Boolean(plan?.specialOffering && isTenureBasedOfferType(plan?.offerType))
}

export function isPriceBasedSpecialOffer(plan: EligiblePlan | undefined): boolean {
  // PRICE_BASED offerType is no longer sent — treat ₹499 effective fee as the discounted-price gift banner
  return Boolean(plan?.specialOffering && plan?.effectivePlanFees?.amount === 499)
}

export function resolveSpecialOfferBanner(
  plan: EligiblePlan,
  bannerType: PlanConfig['bannerType']
): PlanConfig['specialOfferBanner'] {
  if (bannerType === 'TENURE_BASED') {
    return {
      offerValidTill: plan?.offerValidTill ?? undefined,
      extraMonths: resolveTenureBasedExtraMonths(plan),
      baseMonths: resolveTenureBasedBaseMonths(plan?.basePlanTenure)
    }
  }
  if (bannerType === 'PRICE_BASED') {
    return {
      offerValidTill: plan?.offerValidTill ?? undefined
    }
  }
  return undefined
}

export { resolveTenureBasedBaseMonths } from '../../../components/elite/EliteSpecialOfferGiftBanner'

export function resolveTenureBasedExtraMonths(plan: EligiblePlan | undefined): number {
  const effectiveValue = Number(plan?.effectivePlanTenure?.value ?? 0)
  const baseValue = Number(plan?.basePlanTenure?.value ?? 0)
  if (
    plan?.effectivePlanTenure?.period === 'MONTH' &&
    plan?.basePlanTenure?.period === 'MONTH' &&
    effectiveValue > baseValue
  ) {
    return effectiveValue - baseValue
  }
  const tenureDiff = plan?.tenureDiff
  if (typeof tenureDiff !== 'number' || tenureDiff <= 0) return 0
  if (tenureDiff <= 24) return tenureDiff
  return Math.max(1, Math.round(tenureDiff / 30))
}

export function formatSpecialOfferTillLabel(offerValidTill?: number | null): string | undefined {
  if (!offerValidTill) return undefined
  const expiryTimestampMs = offerValidTill < 1e12 ? offerValidTill * 1000 : offerValidTill
  const expiryDate = new Date(expiryTimestampMs)
  const today = new Date()
  const expiryDateOnly = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate())
  const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  if (expiryDateOnly.getTime() === todayDateOnly.getTime()) {
    return 'SPECIAL OFFER ENDS 11:59PM TONIGHT'
  }

  const day = expiryDate.getDate()
  const month = convertEpochToDate(expiryTimestampMs, '%b').toUpperCase()
  const year = expiryDate.getFullYear().toString().slice(-2)
  return `SPECIAL OFFER TILL ${day} ${month} '${year}`
}

export function getBannerType(plan: EligiblePlan): PlanConfig['bannerType'] {
  if (isTenureBasedSpecialOffer(plan)) return 'TENURE_BASED'
  if (isPriceBasedSpecialOffer(plan)) return 'PRICE_BASED'
  if (isLimitedTime14MonthOffer(plan)) return 'LIMITED_TIME_14_MONTH'
  if (isYearly999LimitedTimeOffer(plan)) return 'SAVE_44'
  if (!plan?.specialOffering) return undefined
  return 'SAVE_44'
}

export function resolveSelectedPlanForCta(
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans'],
  selectedPlan: EliteLandingPlan
): EligiblePlan | undefined {
  return eligiblePlans.find((plan) => {
    const resolvedTenure = plan?.effectivePlanTenure ?? plan?.basePlanTenure
    if (selectedPlan === 'yearly') return isYearlyPlanTenure(resolvedTenure)
    return resolvedTenure?.period === 'MONTH' && !isYearlyPlanTenure(resolvedTenure)
  })
}

export function updateCtaWithSelectedPlanId(
  cta: EliteOnboardingStatusResponse['cta'] | EliteInsightsCta | undefined | null,
  planId: string
): Record<string, unknown> {
  const updatedCta: Record<string, unknown> = { ...(cta ?? {}) }
  const requestPayloadString = typeof updatedCta.requestPayload === 'string' ? updatedCta.requestPayload : ''
  if (!requestPayloadString) return updatedCta
  updatedCta.requestPayload = requestPayloadString.split('effectivePlanId').join(planId)
  return updatedCta
}

export function resolvePlanId(plan: EligiblePlan | undefined): string {
  return plan?.effectivePlanId ?? plan?.basePlanId ?? ''
}

export function isLimitedTime14MonthOffer(plan: EligiblePlan): boolean {
  return plan?.effectivePlanTenure?.period === 'MONTH' && Number(plan?.effectivePlanTenure?.value ?? 0) === 14
}

const UnCheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="#C8CCCC" stroke-width="2.5"/>
</svg>`

const CheckedIcon =
  `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9968 7.34881 18.9422 4.80712 17.0676 2.93244C15.1929 1.05776 12.6512 0.00317572 10 0V0ZM14.884 8.384L9.384 13.884C9.26793 14.0001 9.13011 14.0922 8.97844 14.1551C8.82676 14.2179 8.66418 14.2502 8.5 14.2502C8.33582 14.2502 8.17325 14.2179 8.02157 14.1551C7.8699 14.0922 7.73208 14.0001 7.616 13.884L5.116 11.384C4.88155 11.1495 4.74984 10.8316 4.74984 10.5C4.74984 10.1684 4.88155 9.85045 5.116 9.616C5.35046 9.38155 5.66844 9.24984 6 9.24984C6.33157 9.24984 6.64955 9.38155 6.884 9.616L8.5 11.232L13.116 6.616C13.2321 6.49991 13.3699 6.40782 13.5216 6.345C13.6733 6.28217 13.8358 6.24984 14 6.24984C14.1642 6.24984 14.3267 6.28217 14.4784 6.345C14.6301 6.40782 14.7679 6.49991 14.884 6.616C15.0001 6.73209 15.0922 6.86991 15.155 7.02158C15.2178 7.17326 15.2502 7.33583 15.2502 7.5C15.2502 7.66417 15.2178 7.82674 15.155 7.97842C15.0922 8.13009 15.0001 8.26791 14.884 8.384Z" fill="#232323"/>
</svg>`

export function PlanOptionBody({
  plan,
  isSelected,
  onClick,
  suppressPlanChrome,
  singleVisiblePlan,
  borderHandledByParent = false,
  hasBannerAbove = false,
  flushBottom = false
}: {
  plan: PlanConfig
  isSelected: boolean
  onClick: () => void
  suppressPlanChrome?: boolean
  singleVisiblePlan?: boolean
  /** When nested inside PlanOptionCard banner wrapper, selection ring is on the compound card wrapper. */
  borderHandledByParent?: boolean
  hasBannerAbove?: boolean
  flushBottom?: boolean
}) {
  const showPlanCheckbox = !suppressPlanChrome && !singleVisiblePlan
  const insetOutlineClass =
    singleVisiblePlan || borderHandledByParent
      ? ''
      : getPlanCardInsetOutlineClass(isSelected)
  const roundedClass = getPlanOptionBodyRoundedClass({
    borderHandledByParent,
    hasBannerAbove,
    flushBottom
  })
  return (
    <div
      onClick={onClick}
      className={`${roundedClass} bg-white px-4 py-[14px] ${insetOutlineClass} ${singleVisiblePlan || suppressPlanChrome ? '' : 'cursor-pointer'}`}
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          {showPlanCheckbox ? (
            <AnimatedEliteSelectionCheckbox
              checked={isSelected}
              checkedSvg={CheckedIcon}
              uncheckedSvg={UnCheckedIcon}
            />
          ) : null}
          <div className='text-left'>
            <p className='text-sm font-medium text-[#65321D]'>{plan.title}</p>
            {suppressPlanChrome || !plan.subtitle ? null : <p className='text-[13px] font-medium text-[#BB4F22]'>{plan.subtitle}</p>}
          </div>
        </div>
        <div className='text-right'>
          <p className='text-sm font-medium text-[#232323]'>
            {plan.currentPrice}
            <span className='text-[12px] font-normal text-[#8E9393]'>+GST</span>
          </p>
          {suppressPlanChrome || !plan.showStrikethrough || !plan.previousPrice ? null : (
            <p className='text-[13px] text-[#8E9393] line-through'>{plan.previousPrice}</p>
          )}
        </div>
      </div>
    </div>
  )
}

export function PlanTopBanner({
  familyConfig,
  isSelected,
  bannerType,
  offerType,
  signupOffer,
  specialOfferBanner,
  topLabel,
  paymentSuccessTopBanner,
  pendingPaymentTopLabelClock,
  customYearlyBannerLabel
}: {
  familyConfig: FamilyOnboardingViewProps
  isSelected: boolean
  bannerType?: PlanConfig['bannerType']
  offerType?: ElitePlanOfferType
  signupOffer?: boolean
  specialOfferBanner?: PlanConfig['specialOfferBanner']
  topLabel?: string
  paymentSuccessTopBanner?: true
  pendingPaymentTopLabelClock?: true
  customYearlyBannerLabel?: string
}) {
  if (familyConfig.isFamilyMode) {
    return (
      <PlanTopBannerTwoLineStrip
        isSelected={isSelected}
        headline={familyConfig.familyOrangeStripText?.headline}
        subline={familyConfig.familyOrangeStripText?.subline}
        sublineClassName='font-medium'
      />
    )
  }

  if (topLabel && paymentSuccessTopBanner) {
    return (
      <div className='flex items-center justify-center gap-2 py-2.5 text-[13px] font-medium leading-[1.3] text-[#65321D]'>
        <span className='flex size-5 shrink-0 items-center justify-center rounded-full bg-[#65321D] text-[11px] text-white'>
          ✓
        </span>
        {topLabel}
      </div>
    )
  }

  if (topLabel && pendingPaymentTopLabelClock) {
    return (
      <div className='flex items-center justify-center gap-2 py-2.5 text-[13px] font-medium leading-[1.3] text-[#65321D]'>
        <ElitePlanPendingPaymentClockIcon />
        {topLabel}
      </div>
    )
  }

  if (topLabel) {
    return <div className='py-2 text-[13px] leading-[1.3]'>{topLabel}</div>
  }

  if (bannerType === 'TENURE_BASED' && specialOfferBanner) {
    return (
      <EliteSpecialOfferGiftBanner
        variant='TENURE_BASED'
        offerType={offerType}
        signupOffer={signupOffer}
        offerValidTill={specialOfferBanner.offerValidTill}
        baseMonths={specialOfferBanner.baseMonths ?? 12}
        extraMonths={
          specialOfferBanner.extraMonths ??
          (isSignupTenureBasedOffer(offerType, signupOffer) ? 2 : 0)
        }
        isSelected={isSelected}
      />
    )
  }

  if (bannerType === 'PRICE_BASED' && specialOfferBanner) {
    return (
      <EliteSpecialOfferGiftBanner
        variant='PRICE_BASED'
        offerType={offerType}
        signupOffer={signupOffer}
        offerValidTill={specialOfferBanner.offerValidTill}
        isSelected={isSelected}
      />
    )
  }

  if (bannerType === 'LIMITED_TIME_14_MONTH') {
    return (
      <PlanTopBannerTwoLineStrip
        isSelected={isSelected}
        headline='LIMITED TIME OFFER'
        subline={
          <>
            Get yearly for <span className='font-semibold'>2 extra months of Elite</span>
          </>
        }
      />
    )
  }

  if (customYearlyBannerLabel) {
    return (
      <div className={`py-2 text-[13px] leading-[1.3] ${isSelected ? 'text-white' : 'text-[#65321D]'}`}>
        {customYearlyBannerLabel}
      </div>
    )
  }

  return (
    <div className={`py-1 ${isSelected ? 'text-white' : 'text-[#65321D]'}`}>
      <ElitePlanOneLineLimitedTimeOfferContent />
    </div>
  )
}

export function ElitePlanOneLineLimitedTimeOfferContent() {
  return (
    <>
      Special price <span className='font-semibold'>until DIWALI</span>
    </>
  )
}

export function ElitePlanPendingPaymentClockIcon() {
  const clipId = `elite-plan-pending-clock-${useId().replace(/:/g, '')}`
  return (
    <svg
      width={14}
      height={14}
      viewBox='0 0 14 14'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className='size-[14px] shrink-0'
      aria-hidden
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d='M12.8276 7.00133C12.8276 3.78289 10.2185 1.17383 7.00011 1.17383C3.78166 1.17383 1.17261 3.78289 1.17261 7.00133C1.17261 10.2198 3.78168 12.8288 7.00011 12.8288C7.94515 12.8288 8.83765 12.6039 9.62698 12.2046'
          stroke='#65321D'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M11.8843 10.7716C11.5267 10.7716 11.2368 10.4817 11.2368 10.1241C11.2368 9.76646 11.5267 9.47656 11.8843 9.47656C12.2419 9.47656 12.5318 9.76646 12.5318 10.1241C12.5318 10.4817 12.2419 10.7716 11.8843 10.7716Z'
          fill='#65321D'
          stroke='#65321D'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M7 4.41016V7.00016H9.31024'
          stroke='#65321D'
          strokeWidth={1.5}
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width='14' height='14' fill='white' />
        </clipPath>
      </defs>
    </svg>
  )
}

const eliteEsignLearnMoreBodyHtml = `<p class="text-left text-[15px] font-normal leading-[1.5] text-[#6A6E6E]">eSign needs to be completed within 7 days of payment. In case you decide not to proceed, we&apos;ll cancel the membership request and your money will be refunded.</p><p class="mt-4 text-left text-[15px] font-normal leading-[1.5] text-[#6A6E6E]">Note: Your Elite membership will be activated only after you complete eSign. It is mandated by SEBI for all advisory services.</p>`

export function getPlanCardInsetOutlineClass(isSelected: boolean): string {
  if (isSelected) {
    return 'shadow-[inset_0_0_0_1.5px_#CC5C2D]'
  }
  return 'shadow-[inset_0_0_0_1.5px_transparent]'
}

/** Banner + body cards: inset box-shadow is weak on gradient/peach; use inner outline on the full rounded card. */
export function getPlanCardCompoundOutlineClass(isSelected: boolean): string {
  if (isSelected) {
    return 'outline outline-[1.5px] outline-[#CC5C2D] -outline-offset-[1.5px]'
  }
  return 'outline outline-[1.5px] outline-transparent -outline-offset-[1.5px]'
}

export function EliteEsignSupportLearnMoreBottomSheet({
  onDismiss,
  activationExpiryDate,
  buyCtaExtras = {}
}: {
  onDismiss: () => void
  activationExpiryDate?: number
  // Passed by the in-page opener; this sheet renders in a portal and cannot read context.
  buyCtaExtras?: EliteEventExtras
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()

  useEffect(() => {
    trackNewEliteBottomSheetLoaded(
      analyticsScreen,
      NewEliteLandingBottomSheetName.EsignLearnMore,
      buyCtaExtras
    )
  }, [analyticsScreen])

  const heading = activationExpiryDate
    ? `Complete eSign before ${convertEpochToDate(activationExpiryDate, '%d %b')}`
    : 'Complete eSign to activate your membership'

  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      heading={heading}
      bottomSheetHeadClassName=''
      bottomSheetHeadingClassName='!text-left !text-[22px] !font-normal !leading-[1.3] !text-[#232323] !mb-3 !px-5'
      bottomSheetBodyClassName='!px-5 !pt-0 !pb-2'
    >
      <RenderHTML innerHTMLText={eliteEsignLearnMoreBodyHtml} />
    </BottomSheetContent>
  )
}

export function AnimatedEliteSelectionCheckbox({
  checked,
  checkedSvg,
  uncheckedSvg
}: {
  checked: boolean
  checkedSvg: string
  uncheckedSvg: string
}) {
  return (
    <div
      key={checked ? 'checked' : 'unchecked'}
      className='size-5 shrink-0 animate-elite-checkbox-toggle'
    >
      <RenderHTML innerHTMLText={checked ? checkedSvg : uncheckedSvg} />
    </div>
  )
}

const planTopBannerShineIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="176" height="75" viewBox="0 0 176 75" fill="none">
  <g opacity="0.7" filter="url(#filter0_f_39936_7724)">
    <path d="M103.094 -8.44769H164.918L72.9579 123.512L11.1345 123.512L103.094 -8.44769Z" fill="white"/>
  </g>
  <defs>
    <filter id="filter0_f_39936_7724" x="0.134521" y="-19.4478" width="175.783" height="153.96" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
      <feGaussianBlur stdDeviation="5.5" result="effect1_foregroundBlur_39936_7724"/>
    </filter>
  </defs>
</svg>`

const planTopBannerShineKeyframesCss = `
  @keyframes shine {
    0% {
      transform: translateX(-100%) scale(1.25);
      opacity: 1;
    }
    40% {
      transform: translateX(-20%) scale(1.25);
      opacity: 1;
    }
    100% {
      transform: translateX(100%) scale(1.25);
      opacity: 0;
    }
  }
`

export function NewEliteLandingPrimaryCtaButton({
  label,
  disabled,
  onClick,
  className = 'mx-5'
}: {
  label: string
  disabled: boolean
  onClick: () => void
  className?: string
}) {
  console.log(disabled)
  return (
    <div
      onClick={onClick}
      className={`rounded-xl py-4 text-center ${className} ${disabled ? 'bg-[#CDC5C2]' : 'bg-[#232323] cursor-pointer'}`}
    >
      <p key={label} className='animate-elite-cta-label-in text-base font-medium leading-[1.5] text-white'>
        {label}
      </p>
    </div>
  )
}

export function PlanTopBannerTwoLineStrip({
  isSelected,
  headline,
  subline,
  sublineClassName
}: {
  isSelected: boolean
  headline?: ReactNode
  subline: ReactNode
  sublineClassName?: string
}) {
  const [shouldAnimateShine, setShouldAnimateShine] = useState(false)
  const stripRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setShouldAnimateShine(true)
          }, 500)
        }
      },
      { threshold: 0.1 }
    )

    if (stripRef.current) {
      observer.observe(stripRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={stripRef}
      className={`relative overflow-hidden py-2 ${isSelected ? 'text-white' : 'text-[#65321D]'}`}
    >
      <div
        className='pointer-events-none absolute top-0 left-7 z-[1] h-full w-full scale-125'
        style={{
          animation: shouldAnimateShine ? 'shine 1.2s ease-out forwards' : 'none',
          transform: 'translateX(-100%) scale(1.25)',
          opacity: 1
        }}
      >
        <RenderHTML innerHTMLText={planTopBannerShineIconSvg} />
      </div>
      <div className='relative z-10'>
        {headline ? <p className='text-[11px] leading-[1.2] tracking-[0.04em] mb-1'>{headline}</p> : null}
        <p className={`text-13 leading-[1.4] ${sublineClassName ?? ''}`}>{subline}</p>
      </div>
      <style dangerouslySetInnerHTML={{ __html: planTopBannerShineKeyframesCss }} />
    </div>
  )
}

export function getPlanOptionBodyRoundedClass({
  borderHandledByParent,
  hasBannerAbove,
  flushBottom
}: {
  borderHandledByParent: boolean
  hasBannerAbove: boolean
  flushBottom: boolean
}): string {
  if (!borderHandledByParent) return 'rounded-xl'
  if (flushBottom && hasBannerAbove) return 'rounded-none'
  if (flushBottom) return 'rounded-t-xl rounded-b-none'
  return 'rounded-b-xl rounded-t-none'
}

export function hasEliteReferralReward(referralReward?: EliteReferralReward | null): boolean {
  return _hasEliteReferralReward(referralReward)
}

export function YearlyPlanReferralBonusFooter({ referralReward }: { referralReward: EliteReferralReward }) {
  return <EliteReferralRewardBanner referralReward={referralReward} />
}

export function YearlyPlanReferralGiftIcon() {
  return <EliteReferralGiftIcon />
}
